import * as THREE from "three"

export type WallId = "n" | "e" | "s" | "w"
export type RoomShape = "cube" | "cylinder" | "sphere"

export const WALL_IDS: WallId[] = ["n", "e", "s", "w"]

export const WALL_COLORS: Record<WallId, string> = {
  n: "#5B8DEF",
  e: "#E8B44A",
  s: "#E0566B",
  w: "#47B893",
}

const MINIMAP_ACCENT = "#F5A524"

const ROOM = { w: 16, d: 16, h: 9 }
const MARGIN = 0.6
const CYL_R = (ROOM.w * 4) / (2 * Math.PI)
const SPHERE_R = 40
const SPHERE_CENTER: [number, number, number] = [0, ROOM.h / 2, 0]
const SEGS = 96
const PITCH_MAX = 1.45
const MINIMAP_PAD = 12
/** Live preview and viewpoint PNGs share this aspect (letterboxed in the stage). */
const CAPTURE_ASPECT = 16 / 9

const KEYMAP: Record<string, string> = {
  KeyW: "f",
  ArrowUp: "f",
  KeyS: "b",
  ArrowDown: "b",
  KeyA: "l",
  ArrowLeft: "l",
  KeyD: "r",
  ArrowRight: "r",
  KeyR: "u",
  KeyF: "d",
  ShiftLeft: "sh",
  ShiftRight: "sh",
}

export type RoomViewerPreset = {
  labelKey: string
  pos: [number, number, number]
  look: [number, number, number]
}

export const ROOM_VIEWER_PRESETS: RoomViewerPreset[] = [
  { labelKey: "center", pos: [0, 4.5, 0], look: [0, 4.5, -8] },
  { labelKey: "nw", pos: [-6.5, 4.5, 6.5], look: [6.5, 4.5, -6.5] },
  { labelKey: "ne", pos: [6.5, 4.5, 6.5], look: [-6.5, 4.5, -6.5] },
  { labelKey: "sw", pos: [-6.5, 4.5, -6.5], look: [6.5, 4.5, 6.5] },
  { labelKey: "se", pos: [6.5, 4.5, -6.5], look: [-6.5, 4.5, 6.5] },
]

export type WallScreenAnchor = { x: number; y: number }

export type RoomViewerCameraState = {
  pos: [number, number, number]
  yaw: number
  pitch: number
  fov: number
  shape: RoomShape
}

export type RoomViewerHudState = {
  x: number
  y: number
  z: number
  yawDeg: number
  fov: number
}

export type CreateRoomViewerEngineOptions = {
  canvas: HTMLCanvasElement
  stage: HTMLElement
  minimap: HTMLCanvasElement
  onHudUpdate?: (state: RoomViewerHudState) => void
  onHeightChange?: (y: number) => void
  onFovChange?: (fov: number) => void
  onWallAnchorsUpdate?: (anchors: Record<WallId, WallScreenAnchor | null>) => void
}

export type RoomViewerEngine = {
  setWallImage: (wall: WallId, src: string | null) => Promise<void>
  setSpherePanorama: (src: string | null) => Promise<void>
  setShape: (shape: RoomShape, animate?: boolean) => void
  setFov: (v: number) => void
  setHeight: (y: number) => void
  goToPreset: (index: number) => void
  getFov: () => number
  getHeight: () => number
  getCameraState: () => RoomViewerCameraState
  applyCameraState: (next: RoomViewerCameraState) => void
  captureFrame: () => Promise<Blob>
  resize: () => void
  destroy: () => void
}

function clamp(v: number, a: number, b: number) {
  return Math.min(b, Math.max(a, v))
}

function renderTargetToPngBlob(renderer: THREE.WebGLRenderer, target: THREE.WebGLRenderTarget): Promise<Blob> {
  const width = target.width
  const height = target.height
  const buffer = new Uint8Array(width * height * 4)
  renderer.readRenderTargetPixels(target, 0, 0, width, height, buffer)

  const out = document.createElement("canvas")
  out.width = width
  out.height = height
  const ctx = out.getContext("2d")
  if (!ctx) return Promise.reject(new Error("Failed to capture frame"))

  const imageData = ctx.createImageData(width, height)
  // WebGL origin is bottom-left; canvas ImageData is top-left.
  for (let y = 0; y < height; y++) {
    const srcRow = (height - y - 1) * width * 4
    const dstRow = y * width * 4
    imageData.data.set(buffer.subarray(srcRow, srcRow + width * 4), dstRow)
  }
  ctx.putImageData(imageData, 0, 0)

  return new Promise<Blob>((resolve, reject) => {
    out.toBlob((blob) => {
      if (blob) resolve(blob)
      else reject(new Error("Failed to capture frame"))
    }, "image/png")
  })
}

function wallXZ(i: number, s: number, t: number): [number, number] {
  const tc = i * (Math.PI / 2)
  const fx = (ROOM.w / 2) * Math.sin(tc) + s * ROOM.w * Math.cos(tc)
  const fz = -(ROOM.d / 2) * Math.cos(tc) + s * ROOM.w * Math.sin(tc)
  const a = tc + s * (Math.PI / 2)
  const cx = CYL_R * Math.sin(a)
  const cz = -CYL_R * Math.cos(a)
  return [fx + (cx - fx) * t, fz + (cz - fz) * t]
}

export function createRoomViewerEngine(opts: CreateRoomViewerEngineOptions): RoomViewerEngine {
  const { canvas, stage, minimap, onHudUpdate, onHeightChange, onFovChange, onWallAnchorsUpdate } = opts
  const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches

  const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, preserveDrawingBuffer: true })
  renderer.outputColorSpace = THREE.SRGBColorSpace
  const maxAniso = renderer.capabilities.getMaxAnisotropy()

  const scene = new THREE.Scene()
  scene.background = new THREE.Color(0x0a0c0f)

  const camera = new THREE.PerspectiveCamera(60, 16 / 9, 0.05, 100)
  camera.rotation.order = "YXZ"
  camera.position.set(0, 4.5, 0)

  const wallAnchorScratch = new THREE.Vector3()
  const wallAnchorsPad = 24

  let bendT = 0
  let bendTarget = 0
  let mode: RoomShape = "cube"

  const dark1 = 0x14171b
  const dark2 = 0x101317
  const g1 = 0x2b333c
  const g2 = 0x20262d
  const cubeDecor = new THREE.Group()
  const cylDecor = new THREE.Group()
  scene.add(cubeDecor)
  scene.add(cylDecor)

  {
    const f = new THREE.Mesh(new THREE.PlaneGeometry(ROOM.w, ROOM.d), new THREE.MeshBasicMaterial({ color: dark1 }))
    f.rotation.x = -Math.PI / 2
    cubeDecor.add(f)
    const gr = new THREE.GridHelper(ROOM.w, 8, g1, g2)
    gr.position.y = 0.01
    cubeDecor.add(gr)
    const c = new THREE.Mesh(new THREE.PlaneGeometry(ROOM.w, ROOM.d), new THREE.MeshBasicMaterial({ color: dark2 }))
    c.rotation.x = Math.PI / 2
    c.position.y = ROOM.h
    cubeDecor.add(c)
  }
  {
    const f = new THREE.Mesh(new THREE.CircleGeometry(CYL_R, 64), new THREE.MeshBasicMaterial({ color: dark1 }))
    f.rotation.x = -Math.PI / 2
    cylDecor.add(f)
    const gr = new THREE.PolarGridHelper(CYL_R, 8, 5, 64, g1, g2)
    gr.position.y = 0.01
    cylDecor.add(gr)
    const c = new THREE.Mesh(new THREE.CircleGeometry(CYL_R, 64), new THREE.MeshBasicMaterial({ color: dark2 }))
    c.rotation.x = Math.PI / 2
    c.position.y = ROOM.h
    cylDecor.add(c)
  }
  cylDecor.visible = false

  const wallMeshes: Record<WallId, THREE.Mesh> = {} as Record<WallId, THREE.Mesh>
  const texLoader = new THREE.TextureLoader()
  texLoader.setCrossOrigin("anonymous")

  function tuneTexture(tex: THREE.Texture) {
    tex.colorSpace = THREE.SRGBColorSpace
    tex.anisotropy = maxAniso
    tex.minFilter = THREE.LinearMipmapLinearFilter
    tex.needsUpdate = true
  }

  function makePlaceholderTexture(wall: WallId) {
    const color = WALL_COLORS[wall]
    const c = document.createElement("canvas")
    c.width = 1280
    c.height = 720
    const g = c.getContext("2d")
    if (!g) return new THREE.Texture()

    const grad = g.createLinearGradient(0, 0, 0, 720)
    grad.addColorStop(0, "#1d242c")
    grad.addColorStop(1, "#12161b")
    g.fillStyle = grad
    g.fillRect(0, 0, 1280, 720)
    g.strokeStyle = color
    g.lineWidth = 6
    g.strokeRect(14, 14, 1252, 692)
    const tex = new THREE.CanvasTexture(c)
    tuneTexture(tex)
    return tex
  }

  WALL_IDS.forEach((id, i) => {
    const geo = new THREE.BufferGeometry()
    const pos = new Float32Array((SEGS + 1) * 2 * 3)
    const uv = new Float32Array((SEGS + 1) * 2 * 2)
    const idx: number[] = []
    for (let k = 0; k <= SEGS; k++) {
      const u = k / SEGS
      uv[k * 2 * 2] = u
      uv[k * 2 * 2 + 1] = 0
      uv[(k * 2 + 1) * 2] = u
      uv[(k * 2 + 1) * 2 + 1] = 1
      if (k < SEGS) {
        const a = k * 2
        const b = (k + 1) * 2
        const c = (k + 1) * 2 + 1
        const d = k * 2 + 1
        idx.push(a, b, c, a, c, d)
      }
    }
    geo.setIndex(idx)
    geo.setAttribute("position", new THREE.BufferAttribute(pos, 3))
    geo.setAttribute("uv", new THREE.BufferAttribute(uv, 2))
    const mesh = new THREE.Mesh(geo, new THREE.MeshBasicMaterial({ map: makePlaceholderTexture(id) }))
    mesh.frustumCulled = false
    mesh.userData.wallIndex = i
    scene.add(mesh)
    wallMeshes[id] = mesh
  })

  function setBend(t: number) {
    Object.values(wallMeshes).forEach((mesh) => {
      const i = mesh.userData.wallIndex as number
      const pos = mesh.geometry.attributes.position as THREE.BufferAttribute
      for (let k = 0; k <= SEGS; k++) {
        const [x, z] = wallXZ(i, k / SEGS - 0.5, t)
        pos.setXYZ(k * 2, x, 0, z)
        pos.setXYZ(k * 2 + 1, x, ROOM.h, z)
      }
      pos.needsUpdate = true
    })
  }
  setBend(0)

  function makeSpherePlaceholderTexture() {
    const c = document.createElement("canvas")
    c.width = 1024
    c.height = 512
    const g = c.getContext("2d")
    if (!g) return new THREE.Texture()
    const grad = g.createLinearGradient(0, 0, 0, 512)
    grad.addColorStop(0, "#1a222c")
    grad.addColorStop(0.5, "#12161b")
    grad.addColorStop(1, "#0c1014")
    g.fillStyle = grad
    g.fillRect(0, 0, 1024, 512)
    const tex = new THREE.CanvasTexture(c)
    tuneTexture(tex)
    return tex
  }

  const sphereGeo = new THREE.SphereGeometry(SPHERE_R, 64, 32)
  sphereGeo.scale(-1, 1, 1)
  const sphereMat = new THREE.MeshBasicMaterial({ map: makeSpherePlaceholderTexture() })
  const sphereMesh = new THREE.Mesh(sphereGeo, sphereMat)
  // Eye must sit at sphere center for equirect; camera uses SPHERE_CENTER (room mid-height).
  sphereMesh.position.set(...SPHERE_CENTER)
  sphereMesh.visible = false
  sphereMesh.frustumCulled = false
  scene.add(sphereMesh)

  function applyModeVisibility() {
    const sphere = mode === "sphere"
    sphereMesh.visible = sphere
    Object.values(wallMeshes).forEach((mesh) => {
      mesh.visible = !sphere
    })
    if (sphere) {
      cubeDecor.visible = false
      cylDecor.visible = false
    } else {
      cubeDecor.visible = bendT < 0.5
      cylDecor.visible = !cubeDecor.visible
    }
  }

  function updateDecor() {
    applyModeVisibility()
  }

  function snapToSphereCenter() {
    camera.position.set(...SPHERE_CENTER)
    state.vel.set(0, 0, 0)
    state.keys.clear()
    onHeightChange?.(camera.position.y)
  }

  const state = {
    yaw: 0,
    pitch: 0,
    tYaw: 0,
    tPitch: 0,
    vel: new THREE.Vector3(),
    keys: new Set<string>(),
    dragging: false,
    lastX: 0,
    lastY: 0,
  }

  function clampPlanXZ(p: THREE.Vector3) {
    const axisLim = ROOM.w / 2 + (CYL_R - ROOM.w / 2) * bendT - MARGIN
    p.x = clamp(p.x, -axisLim, axisLim)
    p.z = clamp(p.z, -axisLim, axisLim)
    const diag = (ROOM.w / 2) * Math.SQRT2
    const rLim = diag + (CYL_R - diag) * bendT - MARGIN
    const r = Math.hypot(p.x, p.z)
    if (r > rLim) {
      const k = rLim / r
      p.x *= k
      p.z *= k
    }
  }

  function setFov(v: number) {
    camera.fov = clamp(v, 25, 100)
    camera.updateProjectionMatrix()
    onFovChange?.(camera.fov)
  }

  function setHeight(y: number) {
    if (mode === "sphere") return
    camera.position.y = clamp(y, 0.6, ROOM.h - 0.6)
    onHeightChange?.(camera.position.y)
  }

  function updateCamera(dt: number) {
    const s = Math.min(1, dt * 14)
    state.yaw += (state.tYaw - state.yaw) * s
    state.pitch += (state.tPitch - state.pitch) * s
    camera.rotation.set(state.pitch, state.yaw, 0)

    if (mode === "sphere") {
      camera.position.set(...SPHERE_CENTER)
      state.vel.set(0, 0, 0)
      onHeightChange?.(camera.position.y)
      return
    }

    const speed = state.keys.has("sh") ? 10 : 4.5
    const fwd = new THREE.Vector3(-Math.sin(state.yaw), 0, -Math.cos(state.yaw))
    const rgt = new THREE.Vector3(Math.cos(state.yaw), 0, -Math.sin(state.yaw))
    const want = new THREE.Vector3()
    if (state.keys.has("f")) want.add(fwd)
    if (state.keys.has("b")) want.sub(fwd)
    if (state.keys.has("r")) want.add(rgt)
    if (state.keys.has("l")) want.sub(rgt)
    if (state.keys.has("u")) want.y += 1
    if (state.keys.has("d")) want.y -= 1
    if (want.lengthSq() > 0) want.normalize().multiplyScalar(speed)
    state.vel.lerp(want, Math.min(1, dt * 10))
    camera.position.addScaledVector(state.vel, dt)

    clampPlanXZ(camera.position)
    camera.position.y = clamp(camera.position.y, 0.6, ROOM.h - 0.6)
    onHeightChange?.(camera.position.y)
  }

  function goTo(pos: [number, number, number], look: [number, number, number]) {
    if (mode === "sphere") {
      camera.position.set(...SPHERE_CENTER)
    } else {
      camera.position.set(...pos)
    }
    const from = mode === "sphere" ? new THREE.Vector3(...SPHERE_CENTER) : camera.position
    const d = new THREE.Vector3(...look).sub(from)
    state.tYaw = Math.atan2(-d.x, -d.z)
    state.tPitch = Math.atan2(d.y, Math.hypot(d.x, d.z))
    state.yaw = state.tYaw
    state.pitch = state.tPitch
  }

  const mctx = minimap.getContext("2d")
  const MS = minimap.width

  function mapExtent() {
    return ROOM.w / 2 + (CYL_R - ROOM.w / 2) * bendT
  }

  function toMap(x: number, z: number): [number, number] {
    const ext = mapExtent()
    return [MS / 2 + (x / ext) * (MS / 2 - MINIMAP_PAD), MS / 2 + (z / ext) * (MS / 2 - MINIMAP_PAD)]
  }

  function drawMinimap() {
    if (!mctx) return
    mctx.clearRect(0, 0, MS, MS)
    const K = 16
    mctx.beginPath()
    for (let i = 0; i < 4; i++) {
      for (let k = 0; k <= K; k++) {
        const [x, z] = wallXZ(i, k / K - 0.5, bendT)
        const [mx, my] = toMap(x, z)
        if (i === 0 && k === 0) mctx.moveTo(mx, my)
        else mctx.lineTo(mx, my)
      }
    }
    mctx.closePath()
    mctx.fillStyle = "#12161b"
    mctx.fill()

    mctx.lineWidth = 3
    mctx.lineCap = "round"
    WALL_IDS.forEach((id, i) => {
      mctx.strokeStyle = WALL_COLORS[id]
      mctx.beginPath()
      for (let k = 0; k <= K; k++) {
        const [x, z] = wallXZ(i, k / K - 0.5, bendT)
        const [mx, my] = toMap(x, z)
        if (k) mctx.lineTo(mx, my)
        else mctx.moveTo(mx, my)
      }
      mctx.stroke()
    })

    const [cx, cy] = toMap(camera.position.x, camera.position.z)
    const ang = Math.atan2(-Math.cos(state.yaw), -Math.sin(state.yaw))
    const hfov = Math.atan(Math.tan(THREE.MathUtils.degToRad(camera.fov / 2)) * camera.aspect)
    const R = 40
    const grd = mctx.createRadialGradient(cx, cy, 2, cx, cy, R)
    grd.addColorStop(0, "rgba(245,165,36,.45)")
    grd.addColorStop(1, "rgba(245,165,36,0)")
    mctx.fillStyle = grd
    mctx.beginPath()
    mctx.moveTo(cx, cy)
    mctx.arc(cx, cy, R, ang - hfov, ang + hfov)
    mctx.closePath()
    mctx.fill()

    mctx.fillStyle = MINIMAP_ACCENT
    mctx.beginPath()
    mctx.arc(cx, cy, 4, 0, Math.PI * 2)
    mctx.fill()
  }

  function mapTeleport(e: PointerEvent) {
    if (mode === "sphere") return
    const r = minimap.getBoundingClientRect()
    const mx = (e.clientX - r.left) * (MS / r.width)
    const my = (e.clientY - r.top) * (MS / r.height)
    const ext = mapExtent()
    camera.position.x = ((mx - MS / 2) / (MS / 2 - MINIMAP_PAD)) * ext
    camera.position.z = ((my - MS / 2) / (MS / 2 - MINIMAP_PAD)) * ext
    clampPlanXZ(camera.position)
  }

  let mapDrag = false
  let mapPointerId: number | null = null
  const onMapPointerDown = (e: PointerEvent) => {
    mapDrag = true
    mapPointerId = e.pointerId
    minimap.setPointerCapture(e.pointerId)
    mapTeleport(e)
  }
  const onMapPointerMove = (e: PointerEvent) => {
    if (mapDrag) mapTeleport(e)
  }
  const onMapPointerUp = (_e: PointerEvent) => {
    mapDrag = false
    if (mapPointerId != null && minimap.hasPointerCapture?.(mapPointerId)) {
      try {
        minimap.releasePointerCapture(mapPointerId)
      } catch {
        // ignore: capture may already be released
      }
    }
    mapPointerId = null
  }
  minimap.addEventListener("pointerdown", onMapPointerDown)
  minimap.addEventListener("pointermove", onMapPointerMove)
  minimap.addEventListener("pointerup", onMapPointerUp)
  minimap.addEventListener("pointercancel", onMapPointerUp)

  const onPointerDown = (e: PointerEvent) => {
    canvas.setPointerCapture(e.pointerId)
    state.dragging = true
    state.lastX = e.clientX
    state.lastY = e.clientY
    canvas.classList.add("dragging")
    canvas.focus()
  }
  const onPointerMove = (e: PointerEvent) => {
    if (!state.dragging) return
    const dx = e.clientX - state.lastX
    const dy = e.clientY - state.lastY
    state.lastX = e.clientX
    state.lastY = e.clientY
    state.tYaw -= dx * 0.0034
    state.tPitch = clamp(state.tPitch - dy * 0.0034, -PITCH_MAX, PITCH_MAX)
  }
  const onPointerUp = () => {
    state.dragging = false
    canvas.classList.remove("dragging")
  }
  const onWheel = (e: WheelEvent) => {
    e.preventDefault()
    setFov(camera.fov + Math.sign(e.deltaY) * 3)
  }
  const isTypingTarget = (target: EventTarget | null) => {
    if (!(target instanceof HTMLElement)) return false
    if (target.isContentEditable) return true
    const tag = target.tagName
    if (tag === "TEXTAREA" || tag === "SELECT") return true
    if (tag === "INPUT") {
      const type = (target as HTMLInputElement).type
      return type !== "range" && type !== "checkbox" && type !== "radio" && type !== "button" && type !== "submit"
    }
    return false
  }
  const onKeyDown = (e: KeyboardEvent) => {
    if (mode === "sphere") return
    if (isTypingTarget(e.target)) return
    // Only move when the room canvas (or its stage) owns focus — not while typing elsewhere.
    const active = document.activeElement
    if (active && active !== canvas && active !== document.body && !canvas.contains(active)) {
      if (isTypingTarget(active)) return
      if (active instanceof HTMLElement && active.closest("[role='dialog']") && !stage.contains(active)) return
    }
    const k = KEYMAP[e.code]
    if (k) {
      state.keys.add(k)
      e.preventDefault()
    }
  }
  const onKeyUp = (e: KeyboardEvent) => {
    const k = KEYMAP[e.code]
    if (k) state.keys.delete(k)
  }

  canvas.addEventListener("pointerdown", onPointerDown)
  canvas.addEventListener("pointermove", onPointerMove)
  canvas.addEventListener("pointerup", onPointerUp)
  canvas.addEventListener("pointercancel", onPointerUp)
  canvas.addEventListener("wheel", onWheel, { passive: false })
  window.addEventListener("keydown", onKeyDown)
  window.addEventListener("keyup", onKeyUp)

  function resize() {
    const availW = Math.max(stage.clientWidth, 1)
    const availH = Math.max(stage.clientHeight, 1)
    // Same 16:9 letterbox as captureFrame — live framing must match viewpoint PNGs.
    let w = availW
    let h = w / CAPTURE_ASPECT
    if (h > availH) {
      h = availH
      w = h * CAPTURE_ASPECT
    }
    w = Math.floor(w)
    h = Math.floor(h)
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
    renderer.setSize(w, h, true)
    camera.aspect = CAPTURE_ASPECT
    camera.updateProjectionMatrix()
  }

  const resizeObserver = new ResizeObserver(resize)
  resizeObserver.observe(stage)
  resize()

  function updateWallAnchors() {
    if (!onWallAnchorsUpdate) return
    const w = canvas.clientWidth
    const h = canvas.clientHeight
    if (w <= 0 || h <= 0) {
      onWallAnchorsUpdate({ n: null, e: null, s: null, w: null })
      return
    }

    // In sphere mode walls are hidden but cardinal centers still project for generate overlays.
    const bend = mode === "sphere" ? 0 : bendT
    const anchors = {} as Record<WallId, WallScreenAnchor | null>
    WALL_IDS.forEach((id, i) => {
      const [x, z] = wallXZ(i, 0, bend)
      wallAnchorScratch.set(x, ROOM.h / 2, z)
      wallAnchorScratch.project(camera)
      const ndc = wallAnchorScratch
      // Behind camera or outside NDC frustum
      if (ndc.z < -1 || ndc.z > 1 || Math.abs(ndc.x) > 1.15 || Math.abs(ndc.y) > 1.15) {
        anchors[id] = null
        return
      }
      const sx = (ndc.x * 0.5 + 0.5) * w
      const sy = (-ndc.y * 0.5 + 0.5) * h
      if (sx < -wallAnchorsPad || sy < -wallAnchorsPad || sx > w + wallAnchorsPad || sy > h + wallAnchorsPad) {
        anchors[id] = null
        return
      }
      anchors[id] = { x: sx, y: sy }
    })
    onWallAnchorsUpdate(anchors)
  }

  let raf = 0
  let prev = performance.now()
  let destroyed = false
  let capturing = false

  function loop(now: number) {
    if (destroyed) return
    const dt = Math.min(0.05, (now - prev) / 1000)
    prev = now
    if (Math.abs(bendT - bendTarget) > 0.0005) {
      if (mode !== "sphere") {
        bendT += (bendTarget - bendT) * Math.min(1, dt * 5)
        if (Math.abs(bendT - bendTarget) <= 0.0005) bendT = bendTarget
        setBend(bendT)
        updateDecor()
      }
    }
    updateCamera(dt)
    // Skip draw while captureFrame owns the GPU (offscreen 16:9 render target).
    if (!capturing) {
      renderer.render(scene, camera)
      drawMinimap()
      updateWallAnchors()
      const p = camera.position
      const deg = ((Math.round(THREE.MathUtils.radToDeg(state.yaw)) % 360) + 360) % 360
      onHudUpdate?.({
        x: Number(p.x.toFixed(1)),
        y: Number(p.y.toFixed(1)),
        z: Number(p.z.toFixed(1)),
        yawDeg: deg,
        fov: Math.round(camera.fov),
      })
    }
    raf = requestAnimationFrame(loop)
  }
  raf = requestAnimationFrame(loop)

  const wallLoadTokens: Record<WallId, number> = { n: 0, e: 0, s: 0, w: 0 }
  let sphereLoadToken = 0

  async function setWallImage(wall: WallId, src: string | null) {
    const mesh = wallMeshes[wall]
    const mat = mesh.material as THREE.MeshBasicMaterial
    const token = ++wallLoadTokens[wall]
    if (!src) {
      const placeholder = makePlaceholderTexture(wall)
      if (token !== wallLoadTokens[wall] || destroyed) return
      if (mat.map) mat.map.dispose()
      mat.map = placeholder
      mat.needsUpdate = true
      return
    }
    await new Promise<void>((resolve, reject) => {
      texLoader.load(
        src,
        (tex) => {
          // Диалог мог закрыться или URL стены сменился, пока грузилась текстура:
          // не присваиваем к уничтоженному/устаревшему материалу.
          if (destroyed || token !== wallLoadTokens[wall]) {
            tex.dispose()
            resolve()
            return
          }
          tuneTexture(tex)
          if (mat.map) mat.map.dispose()
          mat.map = tex
          mat.needsUpdate = true
          resolve()
        },
        undefined,
        () => reject(new Error(`Failed to load wall texture: ${wall}`)),
      )
    }).catch(() => {
      // keep placeholder on load failure
    })
  }

  async function setSpherePanorama(src: string | null) {
    const mat = sphereMat
    const token = ++sphereLoadToken
    if (!src) {
      const placeholder = makeSpherePlaceholderTexture()
      if (token !== sphereLoadToken || destroyed) return
      if (mat.map) mat.map.dispose()
      mat.map = placeholder
      mat.needsUpdate = true
      return
    }
    await new Promise<void>((resolve, reject) => {
      texLoader.load(
        src,
        (tex) => {
          if (destroyed || token !== sphereLoadToken) {
            tex.dispose()
            resolve()
            return
          }
          tuneTexture(tex)
          if (mat.map) mat.map.dispose()
          mat.map = tex
          mat.needsUpdate = true
          resolve()
        },
        undefined,
        () => reject(new Error("Failed to load sphere panorama texture")),
      )
    }).catch(() => {
      // keep placeholder on load failure
    })
  }

  function resolveShape(): RoomShape {
    if (mode === "sphere") return "sphere"
    return bendTarget >= 0.5 ? "cylinder" : "cube"
  }

  function applyShape(shape: RoomShape, animate: boolean) {
    mode = shape === "sphere" ? "sphere" : shape === "cylinder" ? "cylinder" : "cube"
    if (mode === "sphere") {
      bendTarget = 0
      bendT = 0
      setBend(0)
      snapToSphereCenter()
      applyModeVisibility()
      return
    }
    bendTarget = mode === "cylinder" ? 1 : 0
    if (!animate || reducedMotion) {
      bendT = bendTarget
      setBend(bendT)
    }
    applyModeVisibility()
  }

  return {
    setWallImage,
    setSpherePanorama,
    setShape(shape, animate = true) {
      applyShape(shape, animate)
    },
    setFov,
    setHeight,
    goToPreset(index) {
      const p = ROOM_VIEWER_PRESETS[index]
      if (!p) return
      goTo(p.pos, p.look)
    },
    getFov: () => camera.fov,
    getHeight: () => camera.position.y,
    getCameraState(): RoomViewerCameraState {
      return {
        pos: [camera.position.x, camera.position.y, camera.position.z],
        yaw: state.yaw,
        pitch: state.pitch,
        fov: camera.fov,
        shape: resolveShape(),
      }
    },
    applyCameraState(next) {
      const nextShape: RoomShape = next.shape === "sphere" ? "sphere" : next.shape === "cylinder" ? "cylinder" : "cube"
      applyShape(nextShape, false)
      if (nextShape === "sphere") {
        camera.position.set(...SPHERE_CENTER)
      } else {
        camera.position.set(next.pos[0], next.pos[1], next.pos[2])
        clampPlanXZ(camera.position)
        camera.position.y = clamp(camera.position.y, 0.6, ROOM.h - 0.6)
      }
      state.yaw = next.yaw
      state.pitch = next.pitch
      state.tYaw = next.yaw
      state.tPitch = next.pitch
      camera.rotation.set(state.pitch, state.yaw, 0)
      setFov(next.fov)
      onHeightChange?.(camera.position.y)
    },
    async captureFrame() {
      capturing = true
      const availW = Math.max(stage.clientWidth, 1)
      const availH = Math.max(stage.clientHeight, 1)
      let w = availW
      let h = w / CAPTURE_ASPECT
      if (h > availH) {
        h = availH
        w = h * CAPTURE_ASPECT
      }
      w = Math.floor(w)
      h = Math.floor(h)

      const pr = Math.min(window.devicePixelRatio, 2)
      const prevAspect = camera.aspect
      camera.aspect = CAPTURE_ASPECT
      camera.updateProjectionMatrix()

      // Offscreen target: never resize the visible canvas (avoids CSS/buffer aspect stretch).
      const target = new THREE.WebGLRenderTarget(Math.floor(w * pr), Math.floor(h * pr), {
        colorSpace: THREE.SRGBColorSpace,
      })
      try {
        renderer.setRenderTarget(target)
        renderer.clear()
        renderer.render(scene, camera)
        renderer.setRenderTarget(null)
        return await renderTargetToPngBlob(renderer, target)
      } finally {
        target.dispose()
        camera.aspect = prevAspect
        camera.updateProjectionMatrix()
        capturing = false
      }
    },
    resize,
    destroy() {
      destroyed = true
      capturing = false
      cancelAnimationFrame(raf)
      resizeObserver.disconnect()
      canvas.removeEventListener("pointerdown", onPointerDown)
      canvas.removeEventListener("pointermove", onPointerMove)
      canvas.removeEventListener("pointerup", onPointerUp)
      canvas.removeEventListener("pointercancel", onPointerUp)
      canvas.removeEventListener("wheel", onWheel)
      window.removeEventListener("keydown", onKeyDown)
      window.removeEventListener("keyup", onKeyUp)
      minimap.removeEventListener("pointerdown", onMapPointerDown)
      minimap.removeEventListener("pointermove", onMapPointerMove)
      minimap.removeEventListener("pointerup", onMapPointerUp)
      minimap.removeEventListener("pointercancel", onMapPointerUp)

      Object.values(wallMeshes).forEach((mesh) => {
        const mat = mesh.material as THREE.MeshBasicMaterial
        mat.map?.dispose()
        mat.dispose()
        mesh.geometry.dispose()
        scene.remove(mesh)
      })
      sphereMat.map?.dispose()
      sphereMat.dispose()
      sphereGeo.dispose()
      scene.remove(sphereMesh)
      ;[cubeDecor, cylDecor].forEach((group) => {
        group.traverse((obj) => {
          if (obj instanceof THREE.Mesh || obj instanceof THREE.LineSegments) {
            obj.geometry.dispose()
            const mat = obj.material
            if (Array.isArray(mat)) mat.forEach((m) => m.dispose())
            else mat.dispose()
          }
        })
        scene.remove(group)
      })
      renderer.forceContextLoss()
      renderer.dispose()
    },
  }
}
